import { createSlice } from '@reduxjs/toolkit'

// Shared chart-based interpolation helper. Used for pet age curves that
// aren't a simple multiplier (hamster, puppy/kitten weeks) — given a list of
// [x, y] anchor points sorted by x, linearly interpolates between the two
// nearest points, and extrapolates using the nearest segment's slope for
// values outside the chart's range.
const interpolateChart = (x, points) => {
  if (x <= points[0][0]) {
    const [x0, y0] = points[0]
    const [x1, y1] = points[1]
    const slope = (y1 - y0) / (x1 - x0)
    return y0 + slope * (x - x0)
  }
  for (let i = 0; i < points.length - 1; i++) {
    const [x0, y0] = points[i]
    const [x1, y1] = points[i + 1]
    if (x >= x0 && x <= x1) {
      const slope = (y1 - y0) / (x1 - x0)
      return y0 + slope * (x - x0)
    }
  }
  const [x0, y0] = points[points.length - 2]
  const [x1, y1] = points[points.length - 1]
  const slope = (y1 - y0) / (x1 - x0)
  return y1 + slope * (x - x1)
}

// Hamster months -> human-equivalent years. Hamsters have no single agreed
// scientific formula (their lifespan is too short and breed-dependent for a
// clean multiplier), so this is a chart built from typical veterinary/owner
// reference points, interpolated smoothly between them.
const HAMSTER_CHART = [
  [0, 0], [1, 14], [3, 22], [6, 34], [12, 50], [24, 70], [36, 100], [42, 130],
]

// Puppy/kitten age-in-weeks -> developmental stage + human-equivalent years.
// Deliberately lands on exactly 15 human years at 52 weeks so this matches
// the existing years-based dog/cat formula's first-year value — the two
// calculators should never give contradictory answers at the same age.
const PUPPY_WEEKS_CHART = [
  [0, 0], [2, 1], [4, 3], [8, 5], [12, 9], [24, 12], [39, 14], [52, 15],
]

const getPuppyStage = (weeks) => {
  if (weeks <= 2) return 'Neonatal (newborn)'
  if (weeks <= 4) return 'Transitional'
  if (weeks <= 8) return 'Early Socialization'
  if (weeks <= 12) return 'Socialization Period'
  if (weeks <= 24) return 'Juvenile / Teething'
  if (weeks <= 39) return 'Adolescent'
  return 'Young Adult'
}

const initialState = {
  // Normal Age Calculator
  dateOfBirth: '',
  asOfDate: '',
  result: null,

  // Age Difference
  diffDate1: '',
  diffDate2: '',
  diffResult: null,

  // Chronological Age
  chronoBirthDate: '',
  chronoTestDate: '',
  chronoResult: null,

  // Pet Age - By Years (dog/cat/hamster)
  dogAge: '',
  dogSize: 'medium',
  petType: 'dog',
  dogResult: null,

  // Pet Age - By Weeks (young puppies/kittens)
  weeksAge: '',
  weeksPetType: 'puppy',
  weeksResult: null,

  // Generation
  genBirthYear: '',
  genResult: null,

  // Life in Weeks
  lifeBirthDate: '',
  lifeExpectancy: '75',
  lifeResult: null,

  // Exam Eligibility
  examBirthDate: '',
  examType: 'css',
  examResult: null,

  // General Eligibility
  eligBirthDate: '',
  eligResult: null,

  // Corrected Age
  correctedBirthDate: '',
  correctedDueDate: '',
  correctedResult: null,

  // Islamic/Hijri Age
  hijriBirthDate: '',
  hijriResult: null,

  // Retirement Age - By Birth Year
  retireBirthDate: '',
  retireTargetAge: '65',
  retireResult: null,

  // Retirement Age - By Years of Service (Rule of 80/85/90)
  serviceCurrentAge: '',
  serviceYearsWorked: '',
  serviceRuleTarget: '80',
  serviceRuleResult: null,

  // Pregnancy Due Date
  pregLmpDate: '',
  pregCycleLength: '28',
  pregResult: null,

  activeTab: 'age',
}

const ageSlice = createSlice({
  name: 'age',
  initialState,
  reducers: {
    setActiveTab: (state, action) => {
      state.activeTab = action.payload
    },

    // ---- Normal Age Calculator ----
    setDateOfBirth: (state, action) => {
      state.dateOfBirth = action.payload
    },
    setAsOfDate: (state, action) => {
      state.asOfDate = action.payload
    },
    calculateAge: (state) => {
      if (!state.dateOfBirth) {
        state.result = null
        return
      }

      const birthDate = new Date(state.dateOfBirth)
      const today = state.asOfDate ? new Date(state.asOfDate) : new Date()

      let years = today.getFullYear() - birthDate.getFullYear()
      let months = today.getMonth() - birthDate.getMonth()
      let days = today.getDate() - birthDate.getDate()

      if (days < 0) {
        months--
        const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0)
        days += lastMonth.getDate()
      }
      if (months < 0) {
        years--
        months += 12
      }

      const totalDays = Math.floor((today - birthDate) / (1000 * 60 * 60 * 24))
      const hours = Math.floor((today - birthDate) / (1000 * 60 * 60))
      const minutes = Math.floor((today - birthDate) / (1000 * 60))

      const nextBirthday = new Date(today.getFullYear(), birthDate.getMonth(), birthDate.getDate())
      if (nextBirthday < today) {
        nextBirthday.setFullYear(today.getFullYear() + 1)
      }
      const daysUntilBirthday = Math.ceil((nextBirthday - today) / (1000 * 60 * 60 * 24))

      const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
      const dayOfBirth = daysOfWeek[birthDate.getDay()]

      const getZodiacSign = (date) => {
        const month = date.getMonth() + 1
        const day = date.getDate()
        if ((month === 3 && day >= 21) || (month === 4 && day <= 19)) return 'Aries'
        if ((month === 4 && day >= 20) || (month === 5 && day <= 20)) return 'Taurus'
        if ((month === 5 && day >= 21) || (month === 6 && day <= 20)) return 'Gemini'
        if ((month === 6 && day >= 21) || (month === 7 && day <= 22)) return 'Cancer'
        if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) return 'Leo'
        if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) return 'Virgo'
        if ((month === 9 && day >= 23) || (month === 10 && day <= 22)) return 'Libra'
        if ((month === 10 && day >= 23) || (month === 11 && day <= 21)) return 'Scorpio'
        if ((month === 11 && day >= 22) || (month === 12 && day <= 21)) return 'Sagittarius'
        if ((month === 12 && day >= 22) || (month === 1 && day <= 19)) return 'Capricorn'
        if ((month === 1 && day >= 20) || (month === 2 && day <= 18)) return 'Aquarius'
        return 'Pisces'
      }

      state.result = {
        years, months, days, hours, minutes, totalDays,
        daysUntilBirthday, dayOfBirth,
        zodiacSign: getZodiacSign(birthDate),
      }
    },
    clearAgeResult: (state) => {
      state.result = null
      state.asOfDate = ''
    },

    // ---- Age Difference Calculator ----
    setDiffDate1: (state, action) => {
      state.diffDate1 = action.payload
    },
    setDiffDate2: (state, action) => {
      state.diffDate2 = action.payload
    },
    calculateDifference: (state) => {
      if (!state.diffDate1 || !state.diffDate2) {
        state.diffResult = null
        return
      }
      let d1 = new Date(state.diffDate1)
      let d2 = new Date(state.diffDate2)
      if (d1 > d2) { const tmp = d1; d1 = d2; d2 = tmp }

      let years = d2.getFullYear() - d1.getFullYear()
      let months = d2.getMonth() - d1.getMonth()
      let days = d2.getDate() - d1.getDate()

      if (days < 0) {
        months--
        const lastMonth = new Date(d2.getFullYear(), d2.getMonth(), 0)
        days += lastMonth.getDate()
      }
      if (months < 0) {
        years--
        months += 12
      }

      const totalDays = Math.floor((d2 - d1) / (1000 * 60 * 60 * 24))
      const totalMonths = years * 12 + months
      const totalWeeks = Math.floor(totalDays / 7)

      state.diffResult = { years, months, days, totalDays, totalMonths, totalWeeks }
    },
    clearDifference: (state) => {
      state.diffDate1 = ''
      state.diffDate2 = ''
      state.diffResult = null
    },

    // ---- Chronological Age Calculator ----
    setChronoBirthDate: (state, action) => {
      state.chronoBirthDate = action.payload
    },
    setChronoTestDate: (state, action) => {
      state.chronoTestDate = action.payload
    },
    calculateChronological: (state) => {
      if (!state.chronoBirthDate || !state.chronoTestDate) {
        state.chronoResult = null
        return
      }
      const birthDate = new Date(state.chronoBirthDate)
      const testDate = new Date(state.chronoTestDate)

      let years = testDate.getFullYear() - birthDate.getFullYear()
      let months = testDate.getMonth() - birthDate.getMonth()
      let days = testDate.getDate() - birthDate.getDate()

      if (days < 0) {
        months--
        const lastMonth = new Date(testDate.getFullYear(), testDate.getMonth(), 0)
        days += lastMonth.getDate()
      }
      if (months < 0) {
        years--
        months += 12
      }

      if (days >= 16) {
        months++
        if (months === 12) {
          years++
          months = 0
        }
      }

      state.chronoResult = {
        years, months,
        formatted: `${years} years; ${months} months`,
      }
    },
    clearChronological: (state) => {
      state.chronoBirthDate = ''
      state.chronoTestDate = ''
      state.chronoResult = null
    },

    // ---- Pet Age Calculator ----
    setDogAge: (state, action) => {
      state.dogAge = action.payload
    },
    setDogSize: (state, action) => {
      state.dogSize = action.payload
    },
    setPetType: (state, action) => {
      state.petType = action.payload
    },
    calculatePetAge: (state) => {
      const age = parseFloat(state.dogAge)
      if (!age || age <= 0) {
        state.dogResult = null
        return
      }
      let humanAge
      if (state.petType === 'cat') {
        humanAge = age <= 1 ? age * 15 : age <= 2 ? 15 + (age - 1) * 9 : 24 + (age - 2) * 4
      } else if (state.petType === 'hamster') {
        humanAge = interpolateChart(age * 12, HAMSTER_CHART)
      } else {
        const multipliers = { small: 4, medium: 4.5, large: 5, giant: 6 }
        humanAge = age <= 1 ? age * 15 : age <= 2 ? 15 + (age - 1) * 9 : 24 + (age - 2) * multipliers[state.dogSize]
      }
      state.dogResult = { humanAge: Math.round(humanAge) }
    },
    clearDogAge: (state) => {
      state.dogAge = ''
      state.dogResult = null
    },

    // ---- Pet Age - By Weeks (young puppies/kittens) ----
    setWeeksAge: (state, action) => {
      state.weeksAge = action.payload
    },
    setWeeksPetType: (state, action) => {
      state.weeksPetType = action.payload
    },
    calculateWeeksAge: (state) => {
      const weeks = parseFloat(state.weeksAge)
      if (!weeks || weeks < 0) {
        state.weeksResult = null
        return
      }
      const humanAge = interpolateChart(weeks, PUPPY_WEEKS_CHART)
      state.weeksResult = {
        humanAge: Math.round(humanAge * 10) / 10,
        stage: getPuppyStage(weeks),
      }
    },
    clearWeeksAge: (state) => {
      state.weeksAge = ''
      state.weeksResult = null
    },

    // ---- Generation Calculator ----
    setGenBirthYear: (state, action) => {
      state.genBirthYear = action.payload
    },
    calculateGeneration: (state) => {
      const year = parseInt(state.genBirthYear)
      if (!year || year < 1900 || year > new Date().getFullYear()) {
        state.genResult = null
        return
      }
      let gen
      if (year >= 2013) gen = 'Generation Alpha'
      else if (year >= 1997) gen = 'Generation Z'
      else if (year >= 1981) gen = 'Millennial'
      else if (year >= 1965) gen = 'Generation X'
      else if (year >= 1946) gen = 'Baby Boomer'
      else gen = 'Silent Generation'
      state.genResult = { generation: gen, year }
    },
    clearGeneration: (state) => {
      state.genBirthYear = ''
      state.genResult = null
    },

    // ---- Life in Weeks ----
    setLifeBirthDate: (state, action) => {
      state.lifeBirthDate = action.payload
    },
    setLifeExpectancy: (state, action) => {
      state.lifeExpectancy = action.payload
    },
    calculateLifeLived: (state) => {
      if (!state.lifeBirthDate || !state.lifeExpectancy) {
        state.lifeResult = null
        return
      }
      const birthDate = new Date(state.lifeBirthDate)
      const today = new Date()
      const expectancy = parseFloat(state.lifeExpectancy)
      const totalWeeks = Math.floor(expectancy * 52.1786)
      const livedWeeks = Math.floor((today - birthDate) / (1000 * 60 * 60 * 24 * 7))
      const livedYears = (today - birthDate) / (1000 * 60 * 60 * 24 * 365.25)
      const percentLived = Math.min(100, ((livedYears / expectancy) * 100)).toFixed(1)
      const weeksRemaining = Math.max(0, totalWeeks - livedWeeks)
      state.lifeResult = { totalWeeks, livedWeeks, percentLived, weeksRemaining }
    },
    clearLifeLived: (state) => {
      state.lifeBirthDate = ''
      state.lifeExpectancy = ''
      state.lifeResult = null
    },

    // ---- Exam Age Eligibility ----
    setExamBirthDate: (state, action) => {
      state.examBirthDate = action.payload
    },
    setExamType: (state, action) => {
      state.examType = action.payload
    },
    calculateExamEligibility: (state) => {
      if (!state.examBirthDate) {
        state.examResult = null
        return
      }
      const birthDate = new Date(state.examBirthDate)
      const today = new Date()
      let age = today.getFullYear() - birthDate.getFullYear()
      const m = today.getMonth() - birthDate.getMonth()
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--

      const ranges = {
        css: { min: 21, max: 30, label: 'CSS (Central Superior Services)' },
        ppsc: { min: 21, max: 30, label: 'PPSC' },
        fpsc: { min: 18, max: 28, label: 'FPSC' },
      }
      const r = ranges[state.examType] || ranges.css
      const eligible = age >= r.min && age <= r.max
      const yearsLeftToMax = r.max - age

      state.examResult = { age, eligible, label: r.label, min: r.min, max: r.max, yearsLeftToMax }
    },
    clearExamEligibility: (state) => {
      state.examBirthDate = ''
      state.examResult = null
    },

    // ---- General Age Eligibility Checker ----
    setEligBirthDate: (state, action) => {
      state.eligBirthDate = action.payload
    },
    calculateEligibility: (state) => {
      if (!state.eligBirthDate) {
        state.eligResult = null
        return
      }
      const birthDate = new Date(state.eligBirthDate)
      const today = new Date()
      let age = today.getFullYear() - birthDate.getFullYear()
      const m = today.getMonth() - birthDate.getMonth()
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--

      const milestones = [
        { label: 'Social Media Sign-up (13+)', min: 13 },
        { label: 'Driving License (varies, 18 common)', min: 18 },
        { label: 'Voting Rights (18 in most countries)', min: 18 },
        { label: 'Full-time Employment (18)', min: 18 },
        { label: 'Alcohol Purchase (18-21, varies by country)', min: 21 },
        { label: 'Senior Citizen Discounts (60)', min: 60 },
        { label: 'Pension / Retirement Age (65)', min: 65 },
      ]

      state.eligResult = {
        age,
        milestones: milestones.map(ms => ({
          ...ms,
          eligible: age >= ms.min,
          yearsLeft: Math.max(0, ms.min - age),
        })),
      }
    },
    clearEligibility: (state) => {
      state.eligBirthDate = ''
      state.eligResult = null
    },

    // ---- Corrected Age Calculator ----
    setCorrectedBirthDate: (state, action) => {
      state.correctedBirthDate = action.payload
    },
    setCorrectedDueDate: (state, action) => {
      state.correctedDueDate = action.payload
    },
    calculateCorrectedAge: (state) => {
      if (!state.correctedBirthDate || !state.correctedDueDate) {
        state.correctedResult = null
        return
      }
      const birthDate = new Date(state.correctedBirthDate)
      const dueDate = new Date(state.correctedDueDate)
      const today = new Date()

      const weeksPremature = dueDate > birthDate
        ? Math.round((dueDate - birthDate) / (1000 * 60 * 60 * 24 * 7))
        : 0

      const calcDiff = (from, to) => {
        let years = to.getFullYear() - from.getFullYear()
        let months = to.getMonth() - from.getMonth()
        let days = to.getDate() - from.getDate()
        if (days < 0) {
          months--
          const lastMonth = new Date(to.getFullYear(), to.getMonth(), 0)
          days += lastMonth.getDate()
        }
        if (months < 0) { years--; months += 12 }
        return { years, months, days }
      }

      const chronological = calcDiff(birthDate, today)
      const corrected = weeksPremature > 0 ? calcDiff(dueDate, today) : chronological

      state.correctedResult = { weeksPremature, chronological, corrected }
    },
    clearCorrectedAge: (state) => {
      state.correctedBirthDate = ''
      state.correctedDueDate = ''
      state.correctedResult = null
    },

    // ---- Islamic / Hijri Age Calculator ----
    setHijriBirthDate: (state, action) => {
      state.hijriBirthDate = action.payload
    },
    calculateHijriAge: (state) => {
      if (!state.hijriBirthDate) {
        state.hijriResult = null
        return
      }
      const birthDate = new Date(state.hijriBirthDate)
      const today = new Date()

      const gregorianToHijri = (date) => {
        const y = date.getFullYear(), m = date.getMonth() + 1, d = date.getDate()
        let jd = Math.floor((1461 * (y + 4800 + Math.floor((m - 14) / 12))) / 4) +
          Math.floor((367 * (m - 2 - 12 * Math.floor((m - 14) / 12))) / 12) -
          Math.floor((3 * Math.floor((y + 4900 + Math.floor((m - 14) / 12)) / 100)) / 4) +
          d - 32075
        let l = jd - 1948440 + 10632
        const n = Math.floor((l - 1) / 10631)
        l = l - 10631 * n + 354
        const j = Math.floor((10985 - l) / 5316) * Math.floor((50 * l) / 17719) +
          Math.floor(l / 5670) * Math.floor((43 * l) / 15238)
        l = l - Math.floor((30 - j) / 15) * Math.floor((17719 * j) / 50) -
          Math.floor(j / 16) * Math.floor((15238 * j) / 43) + 29
        const month = Math.floor((24 * l) / 709)
        const day = l - Math.floor((709 * month) / 24)
        const year = 30 * n + j - 30
        return { year, month, day }
      }

      const totalDays = Math.floor((today - birthDate) / (1000 * 60 * 60 * 24))
      const HIJRI_YEAR_DAYS = 354.36707
      const HIJRI_MONTH_DAYS = 29.53059

      const hijriYears = Math.floor(totalDays / HIJRI_YEAR_DAYS)
      const remainingDays = totalDays - hijriYears * HIJRI_YEAR_DAYS
      const hijriMonths = Math.floor(remainingDays / HIJRI_MONTH_DAYS)
      const hijriDays = Math.round(remainingDays - hijriMonths * HIJRI_MONTH_DAYS)

      state.hijriResult = {
        hijriYears, hijriMonths, hijriDays,
        birthHijri: gregorianToHijri(birthDate),
      }
    },
    clearHijriAge: (state) => {
      state.hijriBirthDate = ''
      state.hijriResult = null
    },

    // ---- Retirement Age Calculator - By Birth Year ----
    setRetireBirthDate: (state, action) => {
      state.retireBirthDate = action.payload
    },
    setRetireTargetAge: (state, action) => {
      state.retireTargetAge = action.payload
    },
    calculateRetirement: (state) => {
      if (!state.retireBirthDate || !state.retireTargetAge) {
        state.retireResult = null
        return
      }
      const birthDate = new Date(state.retireBirthDate)
      const targetAge = parseFloat(state.retireTargetAge)
      const today = new Date()

      const retirementDate = new Date(birthDate)
      retirementDate.setFullYear(birthDate.getFullYear() + Math.floor(targetAge))
      retirementDate.setMonth(birthDate.getMonth() + Math.round((targetAge % 1) * 12))

      const diffMs = retirementDate - today
      const isPast = diffMs < 0
      const totalDays = Math.abs(Math.floor(diffMs / (1000 * 60 * 60 * 24)))
      const years = Math.floor(totalDays / 365.25)
      const months = Math.floor((totalDays % 365.25) / 30.44)

      state.retireResult = {
        retirementDate: retirementDate.toISOString().split('T')[0],
        years, months, isPast,
      }
    },
    clearRetirement: (state) => {
      state.retireBirthDate = ''
      state.retireResult = null
    },

    // ---- Retirement Age Calculator - By Years of Service (Rule of 80/85/90) ----
    setServiceCurrentAge: (state, action) => {
      state.serviceCurrentAge = action.payload
    },
    setServiceYearsWorked: (state, action) => {
      state.serviceYearsWorked = action.payload
    },
    setServiceRuleTarget: (state, action) => {
      state.serviceRuleTarget = action.payload
    },
    calculateServiceRule: (state) => {
      const currentAge = parseFloat(state.serviceCurrentAge)
      const yearsWorked = parseFloat(state.serviceYearsWorked)
      const ruleTarget = parseFloat(state.serviceRuleTarget) || 80

      if (!currentAge || currentAge <= 0 || yearsWorked === '' || yearsWorked < 0 || isNaN(yearsWorked)) {
        state.serviceRuleResult = null
        return
      }

      const currentSum = currentAge + yearsWorked
      const eligible = currentSum >= ruleTarget
      const pointsNeeded = ruleTarget - currentSum

      // Each future year adds 1 to age AND 1 to years of service, so the
      // combined score climbs by 2 points per year.
      const yearsUntilEligible = eligible ? 0 : Math.ceil(pointsNeeded / 2)
      const eligibleAge = Math.round(currentAge + yearsUntilEligible)
      const eligibleServiceYears = Math.round(yearsWorked + yearsUntilEligible)

      state.serviceRuleResult = {
        currentSum,
        ruleTarget,
        eligible,
        yearsUntilEligible,
        eligibleAge,
        eligibleServiceYears,
      }
    },
    clearServiceRule: (state) => {
      state.serviceCurrentAge = ''
      state.serviceYearsWorked = ''
      state.serviceRuleResult = null
    },

    // ---- Pregnancy Due Date Calculator ----
    setPregLmpDate: (state, action) => {
      state.pregLmpDate = action.payload
    },
    setPregCycleLength: (state, action) => {
      state.pregCycleLength = action.payload
    },
    calculatePregnancy: (state) => {
      if (!state.pregLmpDate) {
        state.pregResult = null
        return
      }
      const lmp = new Date(state.pregLmpDate)
      const cycleLength = parseInt(state.pregCycleLength) || 28
      const cycleAdjustment = cycleLength - 28

      const dueDate = new Date(lmp)
      dueDate.setDate(dueDate.getDate() + 280 + cycleAdjustment)

      const today = new Date()
      const gestationDays = Math.floor((today - lmp) / (1000 * 60 * 60 * 24)) + cycleAdjustment
      const gestationWeeks = Math.floor(gestationDays / 7)
      const gestationRemDays = gestationDays % 7

      const daysUntilDue = Math.floor((dueDate - today) / (1000 * 60 * 60 * 24))

      const trimester = gestationWeeks < 13 ? 'First' : gestationWeeks < 27 ? 'Second' : 'Third'

      state.pregResult = {
        dueDate: dueDate.toISOString().split('T')[0],
        gestationWeeks, gestationRemDays,
        daysUntilDue, trimester,
      }
    },
    clearPregnancy: (state) => {
      state.pregLmpDate = ''
      state.pregResult = null
    },
  },
})

export const {
  setActiveTab,
  setDateOfBirth, setAsOfDate, calculateAge, clearAgeResult,
  setDiffDate1, setDiffDate2, calculateDifference, clearDifference,
  setChronoBirthDate, setChronoTestDate, calculateChronological, clearChronological,
  setDogAge, setDogSize, setPetType, calculatePetAge, clearDogAge,
  setWeeksAge, setWeeksPetType, calculateWeeksAge, clearWeeksAge,
  setGenBirthYear, calculateGeneration, clearGeneration,
  setLifeBirthDate, setLifeExpectancy, calculateLifeLived, clearLifeLived,
  setExamBirthDate, setExamType, calculateExamEligibility, clearExamEligibility,
  setEligBirthDate, calculateEligibility, clearEligibility,
  setCorrectedBirthDate, setCorrectedDueDate, calculateCorrectedAge, clearCorrectedAge,
  setHijriBirthDate, calculateHijriAge, clearHijriAge,
  setRetireBirthDate, setRetireTargetAge, calculateRetirement, clearRetirement,
  setServiceCurrentAge, setServiceYearsWorked, setServiceRuleTarget, calculateServiceRule, clearServiceRule,
  setPregLmpDate, setPregCycleLength, calculatePregnancy, clearPregnancy,
} = ageSlice.actions

export default ageSlice.reducer